import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users as UsersIcon,
  Plus,
  Edit,
  Trash2,
  Mail,
  Shield,
  Check,
  X,
  Loader2,
  User,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { supabase, getCurrentProfileSafe } from '../lib/supabase';
import toast from 'react-hot-toast';
import { formatDate } from '../lib/dateUtils';

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  role: 'administrator' | 'staff' | 'viewer';
  is_active: boolean;
  created_at: string;
  last_sign_in_at?: string;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'staff' as 'administrator' | 'staff' | 'viewer',
    password: '',
    confirmPassword: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const profile = await getCurrentProfileSafe();
      if (!profile || profile.role !== 'administrator') {
        toast.error('Administrator access required');
        navigate('/', { replace: true });
        return;
      }

      const { data, error } = await supabase.functions.invoke('admin-users', {
        body: { action: 'list' },
      });

      if (error) {
        if (error.context) {
          const body = await error.context.json();
          console.log('ACTUAL EDGE FUNCTION ERROR:', body);
        }
        throw error;
      }
      if (data?.error) throw new Error(data.error);

      setUsers(data.users);
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'Error fetching users');
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingUser && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      if (editingUser) {
        const { data, error } = await supabase.functions.invoke('admin-users', {
          body: {
            action: 'update',
            userId: editingUser.id,
            email: formData.email,
            full_name: formData.full_name,
            role: formData.role,
          },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        toast.success('User updated successfully');
      } else {
        const { data, error } = await supabase.functions.invoke('admin-users', {
          body: {
            action: 'create',
            email: formData.email,
            password: formData.password,
            full_name: formData.full_name,
            role: formData.role,
          },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        toast.success('User created successfully');
      }

      closeModal();
      fetchUsers();
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'Error saving user');
    }
    setLoading(false);
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const { data, error } = await supabase.functions.invoke('admin-users', {
        body: { action: 'delete', userId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'Error deleting user');
    }
  };

  const openModal = (user?: UserProfile) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        full_name: user.full_name,
        role: user.role,
        password: '',
        confirmPassword: '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        full_name: '',
        role: 'staff',
        password: '',
        confirmPassword: '',
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'administrator': return 'bg-red-100 text-red-800';
      case 'staff': return 'bg-blue-100 text-blue-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UsersIcon size={24} />
            Users
          </h1>
          <p className="text-gray-500">Manage system users and permissions</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 btn-primary"
        >
          <Plus size={20} />
          Add User
        </button>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <UsersIcon className="mx-auto text-gray-400" size={48} />
          <p className="mt-2 text-gray-500">No users found</p>
          <p className="text-sm text-gray-400">Create a user to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => (
            <div key={user.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <User size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">{user.full_name}</h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Mail size={14} />
                      {user.email}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openModal(user)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getRoleColor(user.role)}`}>
                  {getRoleLabel(user.role)}
                </span>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  user.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
                {user.last_sign_in_at && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Calendar size={12} />
                    Last login: {formatDate(user.last_sign_in_at)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {!editingUser && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password *
                      </label>
                      <input
                        type="password"
                        required
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        minLength={6}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm Password *
                      </label>
                      <input
                        type="password"
                        required
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="administrator">Administrator - Full Access</option>
                      <option value="staff">Staff - Limited Access</option>
                      <option value="viewer">Viewer - Read Only</option>
                    </select>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={18} className="text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Role Permissions</p>
                      <ul className="text-xs text-blue-600 space-y-1 mt-1">
                        <li><strong>Administrator:</strong> Full access to all features, settings, and user management</li>
                        <li><strong>Staff:</strong> Can manage flats, owners, accounts, transactions, and statements</li>
                        <li><strong>Viewer:</strong> Can view accounts and statements, but cannot make changes</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button type="button" onClick={closeModal} className="flex-1 btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="flex-1 btn-primary flex items-center justify-center gap-2">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;